import type {
  ProgressStatus,
  RoadmapCredentialItem,
} from "../../api/CertRoadmap/types";
import BilingualText from "./BilingualText";
import { localizedText } from "./localizedText";
import type { RoadmapLanguage } from "./types";

type RoadmapCredentialCardProps = {
  item: RoadmapCredentialItem;
  progressStatus?: string;
  onUpdateProgress?: (status: ProgressStatus) => void;
  updating?: boolean;
  language: RoadmapLanguage;
};

const RoadmapCredentialCard = ({
  item,
  progressStatus,
  onUpdateProgress,
  updating,
  language,
}: RoadmapCredentialCardProps) => {
  const text =
    language === "ar"
      ? {
          tip: "نصيحة:",
          inProgress: "قيد التنفيذ",
          completed: "مكتملة",
          markInProgress: "تحديد كقيد التنفيذ",
          markCompleted: "تحديد كمكتملة",
        }
      : {
          tip: "Tip:",
          inProgress: "In progress",
          completed: "Completed",
          markInProgress: "Mark in progress",
          markCompleted: "Mark completed",
        };
  const status = progressStatus || item.status;
  const statusLabel =
    status === "in_progress"
      ? text.inProgress
      : status === "completed"
        ? text.completed
        : status?.replaceAll("_", " ");

  return (
  <article className="flex flex-col gap-4 rounded-2xl border border-black/5 bg-[#f8faf9] p-5 sm:flex-row sm:items-start">
    {item.logo ? (
      <img
        src={item.logo}
        alt={item.code}
        className="size-16 shrink-0 rounded-xl bg-white p-1.5 object-contain"
      />
    ) : (
      <div className="flex size-16 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-bold text-primary">
        {item.code}
      </div>
    )}

    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="w-full text-lg font-bold text-primary">
          <BilingualText value={item.name} language={language} />
        </h3>
        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
          {item.code}
        </span>
        {statusLabel && (
          <span className="rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-bold text-secondary">
            {statusLabel}
          </span>
        )}
      </div>

      <p className="mt-2 text-sm leading-6 text-lightGray">
        <BilingualText value={item.summary} language={language} />
      </p>

      {localizedText(item.doctor_tip, language) && (
        <p className="mt-3 text-sm leading-6 text-primary/80">
          <span className="font-bold text-secondary">{text.tip} </span>
          <BilingualText value={item.doctor_tip} language={language} />
        </p>
      )}

      {onUpdateProgress && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            disabled={updating || progressStatus === "in_progress"}
            onClick={() => onUpdateProgress("in_progress")}
            className="rounded-full border border-black/10 px-4 py-2 text-xs font-bold text-primary transition hover:bg-white disabled:opacity-40"
          >
            {text.markInProgress}
          </button>
          <button
            disabled={updating || progressStatus === "completed"}
            onClick={() => onUpdateProgress("completed")}
            className="rounded-full bg-secondary px-4 py-2 text-xs font-bold text-white transition hover:bg-[#df4315] disabled:opacity-40"
          >
            {text.markCompleted}
          </button>
        </div>
      )}
    </div>
  </article>
  );
};

export default RoadmapCredentialCard;
